<template>
  <v-card
    v-observe-visibility="
      logViews
        ? {
            callback: logView,
            once: true,
            intersection: {
              threshold,
            },
          }
        : false
    "
    ref="cardRef"
    v-ripple="border"
    class="custom"
    min-width="164px"
    max-width="320px"
    @click.stop.prevent="redirect"
    height="100%"
    :flat="border"
    :class="[
      border ? 'border' : 'hover',
      type == ItemType.P ? 'product' : 'treatment',
    ]"
  >
    <div :class="[border && type == ItemType.P ? 'image-border' : '']">
      <v-img
        :src="imageUrl"
        :max-height="type == ItemType.P ? '370px' : '160px'"
      >
      </v-img>
      <div
        class="campaign-container"
        v-if="campaignText"
        :style="campaignValue || redCampaignBadge ? 'background-color: #ea412a ' : ''"
      >
        <p>{{ campaignText }}</p>
      </div>
      <div class="fav-container" v-show="isFavorite">
        <v-btn icon disabled large class="fav-icon">
          <v-icon
            size="36"
            style="position: absolute; color: var(--v-primary-base) !important"
            >fxt-heart-filled</v-icon
          >
          <v-icon size="36" style="position: absolute; color: white !important"
            >fxt-heart</v-icon
          >
        </v-btn>
      </div>
    </div>
    <div
      :class="[border ? 'body-border' : 'no-body-border', 'body-container']"
      v-ripple="!border"
    >
      <div class="title">
        <v-card-title>
          {{ title }}
        </v-card-title>
      </div>
      <v-card-subtitle>
        {{ subtitle.length > 20 ? subtitle.slice(0, 20) + '...' : subtitle }}
      </v-card-subtitle>
      <v-card-actions v-if="showPrice" @click.stop.prevent="addToCart">
        <v-spacer></v-spacer>
        <span
          v-if="stockAmount == 0 && showPrice"
          class="price-container--out-of-stock"
        >
          Ikke på lager
        </span>
        <span
          v-else-if="stockAmount == -1 && showPrice"
          class="price-container--no-more-products"
        >
          Ikke flere produkter tilgjengelig
        </span>
        <span
          :class="[
            type == ItemType.T && showPrice && !campaignValue
              ? 'price-container--fraPrice'
              : '',
          ]"
        >
          <span
            class="price-container--text"
            v-if="type == ItemType.T && showPrice && !campaignValue"
          >
            Fra
          </span>
          <span
            v-if="showPrice"
            :style="
              stockAmount != 0 && stockAmount != -1
                ? ``
                : ` color:var(--v-primary-lighten3)`
            "
            :class="[
              !campaignValue || stockAmount == 0 || stockAmount == -1
                ? 'price-container--price'
                : 'price-container--discountup',
            ]"
            ><span
              v-show="campaignValue && stockAmount != 0 && stockAmount != -1"
              >før </span
            >{{
              price % 1 != 0 ? price.toFixed(2).replace('.', ',') : price + ',-'
            }}
          </span>
        </span>
        <span
          v-if="
            campaignValue && stockAmount != 0 && stockAmount != -1 && showPrice
          "
          class="price-container--discount"
        >
          {{
            campaignValue % 1 != 0
              ? campaignValue.toFixed(2).replace('.', ',')
              : campaignValue + ',-'
          }}
        </span>

        <v-btn
          class="price-container--button"
          :class="[
            isAddedProduct || (isAdded && type == ItemType.T)
              ? 'price-container--button-added'
              : 'price-container--button',
          ]"
          icon
          outlined
          large
          @click.stop.prevent="addToCart"
          v-if="
            stockAmount != 0 && stockAmount != -1 && showPrice && !overrideText
          "
        >
          <v-icon
            size="29"
            v-if="isAddedProduct || (isAdded && type == ItemType.T)"
            class="added"
            >fxt-check</v-icon
          >
          <v-icon v-else size="29" :class="[border ? 'icon-sec' : '']"
            >fxt-plus</v-icon
          >
        </v-btn>
        <v-btn
          v-if="overrideText"
          color="primary"
          elevation="0"
          rounded
          style="text-transform: none"
          @click.stop.prevent="addToCart"
        >
          {{ overrideText }}
        </v-btn>
      </v-card-actions>
    </div>
  </v-card>
</template>

<script>
import Vue from 'vue';
import { ObserveVisibility } from 'vue-observe-visibility';
import ItemType from './enums/ItemType';

Vue.directive('observe-visibility', ObserveVisibility);
export default {
  name: 'B2CItemCard',

  data() {
    return {
      ItemType,
      isAddedProduct: false,
      threshold: 0.33,
    };
  },
  props: {
    /**
     * The unique identifier for the item
     */
    id: {
      type: String,
      required: true,
    },
    /**
     * The type of the item (product/treatment)
     * Possible values: 'P', 'T'
     */
    type: {
      type: String,
      required: true,
      validator(value) {
        return Object.values(ItemType).includes(value);
      },
    },
    /**
     * The URL of the item image.
     */
    imageUrl: {
      type: String,
      required: true,
    },
    /**
     * The name of the item (product or treatment), used in the item card title.
     */
    title: {
      type: String,
      required: true,
    },
    /**
     * If product: the brand name of the item; if treatment: the duration of the treatment.
     * Used in the item card subtitle
     */
    subtitle: {
      type: String,
      required: true,
    },

    /**
     * The price of the item (product or treatment)
     * Used in the card description text
     */
    price: {
      type: Number,
      required: true,
    },

    /**
     * Determines whether to display the price on the card
     * Defaults to true
     */
    showPrice: {
      type: Boolean,
      default: true,
    },
    /**
     * The available stock amount for the product
     * Only applicable if the item is a product
     */
    stockAmount: {
      type: Number,
    },
    /**
     * Text for a campaign associated with the item (optional)
     */
    campaignText: {
      type: String,
    },
    /**
     * Value of discount percentage for a campaign associated with the item (optional)
     */
    campaignValue: {
      type: Number,
    },

    /**
     * We can either pass this boolean value or a discountprice to turn the campaign badge red (optional)
     */
    redCampaignBadge: {
      type: Boolean,
      default: false
    },
    border: {
      type: Boolean,
      default: false,
    },
    isAdded: {
      type: Boolean,
      default: false,
    },
    isFavorite: {
      type: Boolean,
      default: false,
    },
    /**
     * If this prop is passed, the 'Add to cart' button will be replaced by a button
     * with the prop's text. Emits button click event to parent which will handle any logic.
     */
    overrideText: {
      type: String,
      default: null,
    },
    /**
     * If this prop is true, then an event is emitted when the card is visible to the user. It's only emitted once,
     * and is being used to log views.
     */
    logViews: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    redirect() {
      this.$emit('clickCard', {
        id: this.id,
        type: this.type,
      });
    },
    logView(isVisible) {
      if (isVisible) {
        this.$emit('logView', { id: this.id, type: this.type }, 1);
      }
    },
    addToCart() {
      if (
        !this.$refs.cardRef.$el.classList.contains('add-hidden') &&
        (this.stockAmount > 0 || this.type == ItemType.T)
      ) {
        if (this.type == ItemType.P) {
          this.isAddedProduct = true;
          setTimeout(() => {
            this.isAddedProduct = false;
          }, 1000);
        }
        this.$emit('clickButton', {
          id: this.id,
          type: this.type,
        });
      }
    },
  },
};
</script>

<style scoped lang="scss">
.custom:deep(.v-ripple__container) {
  background-color: var(--v-primary-base) !important;
  opacity: 0.1;
}
.v-btn .v-icon.icon-sec {
  color: var(--v-secondary-base) !important;
}
.product {
  .title {
    display: flex !important;
    min-height: 40px !important;
    height: 100% !important;
  }
  .body-container {
    padding: 8px 20px 14px 20px !important;
  }
}

.treatment {
  .title {
    display: flex !important;
    min-height: 40px !important;
  }
  .body-container {
    padding: 12px 20px 16px 20px !important;
  }
}
.v-btn .v-icon {
  color: var(--v-primary-base) !important;
}
.v-btn .v-icon.added {
  color: white !important;
}

.v-card--link:before {
  background: white !important;
}

.v-card__title {
  font-weight: 700 !important;
  font-size: 18px !important;
  font-weight: 700 !important;
  line-height: 20px !important;
  text-align: left;
  word-break: break-word;
  padding: 0px !important;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  margin: auto 0;
  @supports (-webkit-line-clamp: 3) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: initial;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  }
}

.v-card__subtitle {
  text-align: left;
  padding: 0px !important;
  padding-bottom: 0 !important;
  word-break: break-word;
}

.v-card {
  display: flex;
  flex-direction: column;
  border-radius: 0px !important;
}

.v-card__actions {
  margin-top: auto;
  position: relative;
  bottom: 0px;
  right: 0px;
  padding: 0px !important;
  min-height: 44px !important;
}

.price-container {
  margin-top: 5px;
  color: var(--v-primary-base);
  &--price {
    margin-bottom: 0px !important;
    font-weight: 500;
    font-size: 24px;
    line-height: 20px;
    margin: 0px;
    margin-right: 8px;
    margin-left: 4px;
    color: var(--v-primary-base);
    padding: 4px 0px !important;
  }
  &--fraPrice {
    padding: 4px 0px !important;
    display: flex;
    flex: end;
  }
  &--text {
    font-weight: 700;
    font-size: 13px;
    line-height: normal;
    margin-bottom: auto;
    color: var(--v-primary-base);
  }

  &--out-of-stock {
    width: 46px;
    font-size: 13px;
    line-height: 13px;
    text-align: right;
    font-weight: 700;
    padding: 4px 0px;
    margin-right: 2px;
    color: var(--v-primary-lighten3);
  }
  &--no-more-products {
    width: 70px;
    font-size: 13px;
    line-height: 13px;
    text-align: right;
    font-weight: 700;
    padding: 4px 0px;
    margin-right: 2px;
    color: var(--v-primary-lighten3);
  }
  &--button {
    border: 3px solid var(--v-primary-base) !important ;
    padding: 0px;
  }
  &--button-added {
    background: var(--v-primary-base) !important;
    border: 3px solid var(--v-primary-base);
  }
  &--discount {
    color: #ea412a;
    font-weight: 500;
    font-size: 24px;
    line-height: 20px;
    padding-right: 7px;
  }

  &--discountup {
    color: var(--v-primary-base);

    font-weight: 400;
    font-size: 14px;
    line-height: 14px;
    margin-top: -23px;
    position: absolute;
    right: 52px;
  }
}

.campaign-container {
  position: absolute;
  width: 88px;
  height: 68px;
  left: 0px;
  top: 4%;
  clip-path: polygon(0 25%, 100% 0%, 100% 75%, 0% 100%);
  background: var(--v-primary-base);
  color: white;
}
.fav-container {
  position: absolute;
  right: 12px;
  top: 14px;
}
.fav-hidden {
  .fav-container {
    display: none;
  }
}
.add-hidden.treatment {
  .price-container--button {
    display: none;
  }
}

.campaign-container p {
  position: absolute;
  height: 12px;
  left: 0%;
  right: 0%;
  top: calc(50% - 12px / 2);
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 12px;
  text-align: center;
  text-transform: none;
  color: #ffffff;
}

.image-border {
  border: 2px solid #f8efe8;
}

.body-border {
  display: flex;
  flex-direction: column;
  height: 100%;
  border-right: 3px solid;
  border-bottom: 3px solid;
  border-left: 3px solid;
  border-image-slice: 1;
  border-image-source: linear-gradient(
    180deg,
    #efddd1 0%,
    #fbf1c6 49.76%,
    #e2f1f3 100%
  );
  color: var(--v-primary-base) !important;
  .v-icon {
    color: var(--v-secondary-base) !important;
  }
  .price-container {
    &--button {
      border: 3px solid var(--v-secondary-base) !important ;
    }
    &--button-added {
      background: var(--v-secondary-base) !important;
    }
  }
}

.no-body-border {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.hover {
  transition: 0.3s ease-in-out;
  font-family: Quicksand;
}
.border {
  font-family: Niveau Grotesk;
}
.v-card.hover:hover {
  box-shadow: 0px 8px 10px 0px rgba(0, 0, 0, 0.2);
}

.v-card.border:hover {
  .body-border {
    border-image-source: linear-gradient(#579fa7 0%, #579fa7 100%);
  }

  .v-image::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: linear-gradient(
      0deg,
      var(--v-primary-base) 0%,
      var(--v-primary-base) 100%
    );
    opacity: 0.12;
    transition: opacity 0.3s ease;
  }

  .v-image:hover::before {
    opacity: 0.12;
  }
}
.img-fill {
  opacity: 0.7 !important;
}
@media (max-width: 340px) {
  .price-container {
    &--price {
      margin-right: 2px !important;
      margin-left: 2px !important ;
    }
  }
}
@media (max-width: 580px) {
  .fav-container {
    .v-icon {
      font-size: 24px !important;
    }
    right: -4px;
    top: 8px;
  }
  .v-card__title {
    font-size: 14px !important;
    line-height: 14px !important;
  }
  .v-card__subtitle {
    font-size: 12px !important;
    line-height: 18px;
  }
  .product {
    .body-container {
      padding: 5px 10px 7px 10px !important;
    }
  }

  .treatment {
    .body-container {
      padding: 4px 10px 16px 10px !important;
    }
  }
}
</style>
